import gsap from 'gsap';

import TextPlugin from 'gsap/TextPlugin';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { createStableResizeHandler } from '../viewport-utils';
import { getStaticAssetUrl } from '@/app/lib/utils/static-asset-url';

gsap.registerPlugin(TextPlugin);

export default function initAnimations(
    container: HTMLElement,
    inputContainer: HTMLElement,
    inputText: string,
    button: HTMLElement,
    batchImageContainer: HTMLDivElement,
    batchImages: HTMLCanvasElement,
    batchGenerateText: HTMLElement,
) {
    // 设置初始状态，按钮背景色为半透明白色，输入框边框为灰色
    ScrollTrigger.create({
        trigger: container,
        start: 'top top',
        end: '+=150%',
        scrub: 1,
        pin: true,
        // markers: true,
        id: 'container',
    });

    gsap.set(button, { backgroundColor: '#333333' });
    gsap.set(inputContainer, { borderColor: '#333333' });
    gsap.set(batchGenerateText, { opacity: 0 });

    // gsap.to(
    //     inputContainer,
    //     { opacity: 0 },
    //     {
    //         scrollTrigger: {
    //             trigger: inputContainer,
    //             start: 'bottom bottom',
    //             end: 'center center',
    //             scrub: 1,
    //             id: 'container',
    //             markers: true,
    //         },
    //         opacity: 1,
    //     },
    // );

    ScrollTrigger.create({
        trigger: inputContainer,
        start: 'bottom bottom',
        end: 'center center',
        scrub: true,
        // markers: {
        //     indent: 200,
        // },
        onUpdate: (self) => {
            document.documentElement.style.setProperty(
                '--batch-input-opacity',
                `${self.progress}`,
            );
        },
        id: 'container',
    });

    // 第一步，输入框边框从灰色变为透明，展示本身颜色
    gsap.timeline({
        scrollTrigger: {
            trigger: inputContainer,
            start: 'center center',
            end: '+=2%',
            scrub: true,
            // markers: {
            //     indent: 200,
            // },
            onUpdate: (self) => {
                if (self.progress <= 0.15) {
                    gsap.set(inputContainer, {
                        borderColor: '#333333',
                    });

                    return;
                }

                gsap.set(inputContainer, {
                    borderColor: 'transparent',
                });
            },
            id: 'input-border',
        },
    });

    // 第二步，输入框文字逐渐展示，并修改button颜色
    gsap.timeline({
        scrollTrigger: {
            trigger: '#batch-generate-input-text',
            start: 'center center-=4%',
            end: '+=30%',
            scrub: true,
            // markers: {
            //     indent: 0,
            // },
            id: 'input-text&button',
            onUpdate: (self) => {
                if (self.progress <= 0.15) {
                    gsap.set(button, {
                        backgroundColor: '#333333',
                    });
                    return;
                }

                gsap.set(button, {
                    backgroundColor: '#6038FF',
                });
            },
        },
    }).to('#batch-generate-input-text', {
        text: { value: inputText, rtl: false },
        opacity: 1,
    });

    gsap.timeline({
        scrollTrigger: {
            trigger: '#batch-generate-input-text',
            start: 'center center-=34%',
            end: '+=2%',
            scrub: true,
            // markers: {
            //     indent: 600,
            //     startColor: 'white',
            //     endColor: 'white',
            // },
            id: 'input-button-bg',
        },
    }).to(button, {
        backgroundColor: '#4D35B6',
    });

    // gsap.timeline({
    //     scrollTrigger: {
    //         trigger: '#batch-generate-input-text',
    //         start: 'center center-=36%',
    //         end: '+=10%',
    //         scrub: true,
    //         markers: {
    //             indent: 600,
    //             startColor: 'white',
    //             endColor: 'white',
    //         },
    //         fastScrollEnd: true,
    //         id: 'input',
    //         onUpdate: (self) => {
    //             console.log(
    //                 'opacity',
    //                 self.progress,
    //                 gsap.getProperty(inputContainer, 'opacity'),
    //             );

    //             document.documentElement.style.setProperty(
    //                 '--batch-input-opacity',
    //                 `${1 - self.progress}`,
    //             );
    //         },
    //     },
    // });

    ScrollTrigger.create({
        trigger: '#batch-generate-input-text',
        start: 'center center-=36%',
        end: '+=10%',
        scrub: true,
        // markers: {
        //     indent: 600,
        //     startColor: 'white',
        //     endColor: 'white',
        // },
        fastScrollEnd: true,
        id: 'input',
        onUpdate: (self) => {
            // console.log(
            //     'opacity',
            //     self.progress,
            //     gsap.getProperty(inputContainer, 'opacity'),
            // );

            document.documentElement.style.setProperty(
                '--batch-input-opacity',
                `${1 - self.progress}`,
            );
        },
    });

    // 第三步，背景图片展示
    const cleanupGenerateImageAnimation = initGenerateImageAnimation(
        batchImageContainer,
        batchImages,
    );

    // 第四步，文字展示
    gsap.timeline({
        scrollTrigger: {
            trigger: '#batch-generate-input-text',
            start: 'center center-=65%',
            end: '+=20%',
            scrub: true,
            // markers: {
            //     indent: 200,
            // },
        },
    }).to(batchGenerateText, {
        opacity: 1, // 从透明开始
        ease: 'sine.inOut',
    });

    return cleanupGenerateImageAnimation;
}

function updateOpacity(element: HTMLElement, opacity: string) {
    requestAnimationFrame(() => {
        element.style.opacity = opacity;
    });
}

function initGenerateImageAnimation(
    container: HTMLDivElement,
    canvas: HTMLCanvasElement,
) {
    const frameCount = 242;
    canvas.width = container.clientWidth;
    canvas.height = container.clientHeight;
    const context = canvas.getContext('2d')!;
    const currentFrame = (index: number) =>
        getStaticAssetUrl(
            `/batch_generate/${(index + 1).toString().padStart(5, '0')}.jpg`,
        );

    const images: HTMLImageElement[] = [];

    const bgs = {
        frame: 0,
    };

    for (let i = 0; i < frameCount; i++) {
        const img = new Image();
        img.src = currentFrame(i);
        images.push(img);
    }

    gsap.to(bgs, {
        frame: frameCount - 1,
        snap: 'frame',
        ease: 'none',
        scrollTrigger: {
            trigger: '#batch-generate-input-text',
            start: 'center center-=36%',
            end: '+=30%',
            scrub: true,
            // markers: true,
            id: 'bg',
            onUpdate: (self) => {
                render(); // use animation onUpdate instead of scrollTrigger's onUpdate
            },
        },
    });

    images[0].onload = render;

    function render() {
        if (canvas.width != container.clientWidth) {
            canvas.width = container.clientWidth;
            canvas.height = container.clientHeight;
        }

        const img = images[bgs.frame];
        const canvasRatio = canvas.width / canvas.height;
        const imgRatio = img.naturalWidth / img.naturalHeight;
        let drawWidth, drawHeight, sx, sy, sWidth, sHeight;

        if (imgRatio > canvasRatio) {
            // 图片比画布更宽
            drawHeight = canvas.height;
            drawWidth = canvas.height * imgRatio;
            sWidth = img.naturalHeight * canvasRatio;
            sHeight = img.naturalHeight;
            sx = (img.naturalWidth - sWidth) / 2;
            sy = 0;
        } else {
            // 图片比画布更高
            drawWidth = canvas.width;
            drawHeight = canvas.width / imgRatio;
            sWidth = img.naturalWidth;
            sHeight = img.naturalWidth / canvasRatio;
            sx = 0;
            sy = (img.naturalHeight - sHeight) / 2;
        }

        context.clearRect(0, 0, canvas.width, canvas.height);
        context.drawImage(
            img,
            sx,
            sy,
            sWidth,
            sHeight,
            0,
            0,
            canvas.width,
            canvas.height,
        );
    }

    const handleResize = createStableResizeHandler(() => {
        canvas.width = container.clientWidth;
        canvas.height = container.clientHeight;
        render(); // 确保在窗口大小改变时重新渲染画布
    });
    window.addEventListener('resize', handleResize);

    return () => {
        window.removeEventListener('resize', handleResize);
    };
}
